
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import { Outlet } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import { NewsletterSection } from "@/components/home/NewsletterSection";
import { toast } from "sonner";

const Layout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showNewsletter, setShowNewsletter] = useState(false);
  const [pageLoadTime, setPageLoadTime] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState<boolean>(false);

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      if (typeof window !== 'undefined') {
        setIsMobile(window.innerWidth < 768);
      }
    };
    
    checkMobile();
    
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', checkMobile);
      return () => window.removeEventListener('resize', checkMobile);
    }
  }, []);

  // Debug log to check current path
  useEffect(() => {
    console.log("Layout loaded for path:", location.pathname);
    
    if (typeof window === 'undefined') return;
    
    // Measure page load time
    const startTime = performance.now();
    const onLoad = () => {
      const loadTime = Math.round(performance.now() - startTime);
      setPageLoadTime(loadTime);
      console.log(`Page ${location.pathname} loaded in ${loadTime}ms`);
      
      // Alert if page load is very slow
      if (loadTime > 3000 && process.env.NODE_ENV === 'development') {
        console.warn(`Slow page load detected: ${loadTime}ms for ${location.pathname}`);
      }
    };
    
    // Measure critical resource loading time
    if (window.performance && window.performance.getEntriesByType) {
      setTimeout(() => {
        const resources = window.performance.getEntriesByType('resource');
        const slowResources = resources
          .filter(r => r.duration > 300)
          .sort((a, b) => b.duration - a.duration)
          .slice(0, 5);
        
        if (slowResources.length > 0) {
          console.log('Slow loading resources:');
          slowResources.forEach(r => console.log(`${r.name}: ${Math.round(r.duration)}ms`));
        }
      }, 2000);
    }
    
    window.addEventListener('load', onLoad);
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('load', onLoad);
      }
    };
  }, [location.pathname]);

  // Scroll to top when route changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo(0, 0);
    }
  }, [location.pathname]);

  // This code ensures the base URL works correctly regardless of whether the URL is lainasampo.fi or something else
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    // Check if current page is lainasampo.fi without a path
    const isRootDomain = window.location.pathname === '/' && (
      window.location.hostname === 'lainasampo.fi' || 
      window.location.hostname === 'www.lainasampo.fi'
    );
    
    if (isRootDomain) {
      // If we're at the root level, redirect to the home page
      navigate('/');
    }

    // Handle cases where the URL might not be found - we'll catch it before the 404 page
    const handleOnError = (event: ErrorEvent) => {
      console.error("Error caught in Layout, details:", event.message, "Path:", location.pathname);
      
      // Only redirect on specific navigation/routing errors, not all JS errors
      if (event.message.includes('navigation') || event.message.includes('route') || event.message.includes('chunk')) {
        console.log("Navigation-related error, redirecting to /kilpailuta from:", location.pathname);
        navigate('/kilpailuta', {
          replace: true
        });
      }
    };
    
    window.addEventListener('error', handleOnError);
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('error', handleOnError);
      }
    };
  }, [navigate, location.pathname]);

  // Check for broken links on the page
  const checkBrokenLinks = useCallback(() => {
    if (process.env.NODE_ENV !== 'development' || typeof document === 'undefined') return;
    
    setTimeout(() => {
      const links = document.querySelectorAll('a');
      const brokenLinks: string[] = [];
      
      links.forEach(link => {
        const href = link.getAttribute('href');
        if (href && href.startsWith('/') && !href.includes('#') && href !== '/') {
          // Check if this is an internal link that might be broken
          const route = href.split('?')[0];
          if (window.history.state?.routes && !window.history.state.routes.includes(route)) {
            brokenLinks.push(href);
          }
        }
      });
      
      if (brokenLinks.length > 0) {
        console.warn('Potential broken internal links detected:', brokenLinks);
      }
    }, 1000);
  }, []);

  // Setup link checking on page load
  useEffect(() => {
    if (typeof document !== 'undefined') {
      checkBrokenLinks();
    }
  }, [location.pathname, checkBrokenLinks]);

  // Show newsletter section when footer link is clicked
  useEffect(() => {
    // Check for #newsletter in the hash on component mount and when location changes
    if (location.hash === '#newsletter') {
      console.log("Newsletter hash detected, showing newsletter section");
      setShowNewsletter(true);
      // Scroll to newsletter section with a small delay to ensure it's rendered
      setTimeout(() => {
        if (typeof document !== 'undefined') {
          document.getElementById('newsletter-section')?.scrollIntoView({
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, [location.hash, location]); // Added location.hash as dependency
  
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-grow w-full">
        <Outlet />
        
        {/* Newsletter section will be visible when accessed via footer link */}
        <div id="newsletter-section" className={showNewsletter ? "block" : "hidden"}>
          <NewsletterSection />
        </div>
      </main>
      
      {/* Telemetry indicator for development */}
      {process.env.NODE_ENV === 'development' && pageLoadTime !== null && (
        <div className="fixed bottom-2 right-2 bg-white dark:bg-gray-800 shadow rounded-full px-3 py-1 text-xs opacity-70 hover:opacity-100 z-50">
          {pageLoadTime < 1000 ? (
            <span className="text-green-600">{pageLoadTime}ms</span>
          ) : pageLoadTime < 3000 ? (
            <span className="text-amber-600">{pageLoadTime}ms</span>
          ) : (
            <span className="text-red-600">{pageLoadTime}ms</span>
          )}
          {isMobile && <span className="ml-1 text-blue-500">📱</span>}
        </div>
      )}
      
      <Footer />
    </div>
  );
};

export default Layout;
